import { useBrowserBackDismiss } from "@/hooks/use-browser-back-dismiss";
import { useCenterIntakeObligations } from "@/api/center-intake-obligations";
import {
  CenterIntakeObligationsSection,
  type ReturningFilter,
} from "./center-intake-obligations-section";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { TaskInvestigations } from "./task-investigations";
import { CollectionExceptions } from "./collection-exceptions";
import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";

import {
  type CarrierTask,
  type CarrierTrip,
  useCarrierDispatchTimeWindows,
  useCarrierMe,
  useCarrierOperators,
  useCarrierTaskBacklog,
  useCarrierTrips,
} from "@/api/carrier";
import { PortalState } from "@/components/portal-state";
import { ProtectedRoute } from "@/components/protected-route";
import { DispatchCenterFilter } from "@/components/dispatch-center-filter";
import { Button } from "@/components/ui/button";
import { NativeSelect } from "@/components/ui/native-select";
import { AssignOperatorDialog } from "@/features/assign-operator-dialog";
import { hasHttpStatus } from "@/lib/api-error";
import { isTaskOnCancellationHold } from "@/lib/cancellation-work-hold";
import { COMPACT_VIEWPORT_QUERY, useMediaQuery } from "@/lib/responsive";
import { useSocket } from "@/services/socket/socket-context";
import { useOptionalDispatchCenterScope } from "@/services/dispatch-center-scope";
import {
  CommandBar,
  DispatchDateInput,
  type DispatchWindowOption,
  KpiStrip,
} from "./command-bar";
import { dispatchFreshness } from "./dispatch-live-state";
import {
  type DispatchWindowFilter,
  dispatchDateKey,
  dispatchWindowFilterValue,
  filterDispatchTasks,
  parseDispatchWindowFilter,
} from "./dispatch-read-model";
import { AssignPanel, TripFocusModal } from "./dispatch-panels";
import { isOpenTask, isOpenTrip } from "./dispatch-presenters";
import { DispatchInlineState } from "./dispatch-state";
import { MapShell } from "./map-shell";
import {
  MobileDispatchWorkspace,
  type MobileDispatchWorkspaceKind,
} from "./mobile-dispatch-workspace";
import { LookupPanel } from "./lookup-panel";
import { OperatorRail } from "./operator-rail";
import { TaskFailureDialog } from "./task-failure-dialog";
import type { TaskFailureTarget } from "./task-failure";
import {
  removeStagedTask,
  isTaskSelectableForStaging,
  stageTasks as mergeStagedTasks,
  stagedDispatchCenterId,
} from "./task-staging";

type DispatchPanel = { kind: "assign" } | { kind: "lookup" } | null;

function DispatchExitButton() {
  const navigate = useNavigate();
  return (
    <Button
      type="button"
      variant="outline"
      aria-label="Exit to dashboard"
      onClick={() => navigate("/fulfillment/orders")}
    >
      Exit portal
    </Button>
  );
}

function DispatchV2Content() {
  const queryClient = useQueryClient();
  const returningQuery = useCenterIntakeObligations(1, "", "returning");
  const [returning, setReturning] = useState<ReturningFilter | null>(null);
  useBrowserBackDismiss({
    open: returning !== null,
    onDismiss: () => setReturning(null),
  });
  const returningSummary = returningQuery.isError
    ? []
    : (returningQuery.data?.returningSummary ?? []);
  const [railCollapsed, setRailCollapsed] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [dateChosenByUser, setDateChosenByUser] = useState(false);
  const [selectedWindow, setSelectedWindow] =
    useState<DispatchWindowFilter>(null);
  const [modalTrip, setModalTrip] = useState<CarrierTrip | null>(null);
  const [assignDriverTrip, setAssignDriverTrip] = useState<CarrierTrip | null>(
    null
  );
  const [panel, setPanel] = useState<DispatchPanel>(null);
  const [mobileWorkspace, setMobileWorkspace] = useState<Exclude<
    MobileDispatchWorkspaceKind,
    "assign" | "lookup"
  > | null>(null);
  const [stagedTasks, setStagedTasks] = useState<CarrierTask[]>([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [failureTask, setFailureTask] = useState<TaskFailureTarget | null>(
    null
  );
  const [refreshPending, setRefreshPending] = useState(false);
  const [refreshFeedback, setRefreshFeedback] = useState<
    "success" | "error" | null
  >(null);
  const [freshnessNow, setFreshnessNow] = useState(() => Date.now());
  const { connectionState, lastDisconnectedAt } = useSocket();
  const compactViewport = useMediaQuery(COMPACT_VIEWPORT_QUERY);

  const me = useCarrierMe();
  const { dispatchCenterId } = useOptionalDispatchCenterScope();
  const operationalTimeZone = useMemo(() => {
    const centers =
      me.data?.carriers
        .flatMap((carrier) => carrier.allowedDispatchCenters)
        .filter((center) => center.isActive !== false) ?? [];
    if (dispatchCenterId) {
      return centers.find((center) => center.id === dispatchCenterId)?.timeZone;
    }
    const timeZone = centers[0]?.timeZone;
    return timeZone && centers.every((center) => center.timeZone === timeZone)
      ? timeZone
      : null;
  }, [dispatchCenterId, me.data?.carriers]);
  useEffect(() => {
    if (dateChosenByUser) return;
    setSelectedDate(
      operationalTimeZone
        ? (dispatchDateKey(new Date(), operationalTimeZone) ?? "")
        : ""
    );
  }, [operationalTimeZone, dateChosenByUser]);
  const changeSelectedDate = (date: string) => {
    if (!date) return;
    setDateChosenByUser(true);
    setSelectedDate(date);
  };
  const tasksQuery = useCarrierTaskBacklog({
    excludeAwaitingReceipt: true,
    limit: 200,
  });
  const tripsQuery = useCarrierTrips({ limit: 100 });
  const operatorsQuery = useCarrierOperators({ limit: 200 });
  const awaitingReceiptQuery = useCarrierTaskBacklog({
    awaitingReceipt: true,
    limit: 50,
  });
  const timeWindowsQuery = useCarrierDispatchTimeWindows();

  const lastDataRefreshAt = Math.min(
    tasksQuery.dataUpdatedAt,
    tripsQuery.dataUpdatedAt,
    operatorsQuery.dataUpdatedAt,
    awaitingReceiptQuery.dataUpdatedAt,
    timeWindowsQuery.dataUpdatedAt
  );
  const liveState = dispatchFreshness({
    connectionState,
    lastDataRefreshAt,
    lastDisconnectedAt,
    now: freshnessNow,
  });

  const tasks = tasksQuery.data?.data ?? [];
  const trips = tripsQuery.data?.data ?? [];
  const operators = operatorsQuery.data?.data ?? [];
  const awaitingReceiptTasks = awaitingReceiptQuery.data?.data ?? [];

  const openTasks = useMemo(() => tasks.filter(isOpenTask), [tasks]);
  const windows = useMemo<DispatchWindowOption[]>(
    () =>
      (timeWindowsQuery.data ?? []).map((window) => ({
        value: `${window.startTime}|${window.endTime}` as const,
        label: `${window.startTime}–${window.endTime}`,
      })),
    [timeWindowsQuery.data]
  );
  const visibleTasks = useMemo(
    () =>
      filterDispatchTasks(openTasks, {
        date: selectedDate,
        window: selectedWindow,
      }),
    [openTasks, selectedDate, selectedWindow]
  );
  const visibleAwaitingReceiptTasks = useMemo(
    () =>
      filterDispatchTasks(awaitingReceiptTasks, {
        date: selectedDate,
        window: selectedWindow,
      }),
    [awaitingReceiptTasks, selectedDate, selectedWindow]
  );
  const activeTrips = useMemo(() => trips.filter(isOpenTrip), [trips]);
  const unassignedTasks = useMemo(
    () => visibleTasks.filter((task) => !task.tripId),
    [visibleTasks]
  );
  const dataLoading =
    tasksQuery.isLoading ||
    tripsQuery.isLoading ||
    operatorsQuery.isLoading ||
    awaitingReceiptQuery.isLoading ||
    timeWindowsQuery.isLoading;
  const dispatchErrors = [
    tasksQuery.error,
    tripsQuery.error,
    operatorsQuery.error,
    awaitingReceiptQuery.error,
    timeWindowsQuery.error,
  ];
  const dataError = dispatchErrors.find(Boolean) ?? null;
  const forbidden =
    me.data?.capabilities.canAccessDispatchWeb === false ||
    dispatchErrors.some((error) => hasHttpStatus(error, 403));

  const carrierLabel =
    me.data?.carriers
      .map((carrier) => carrier.name)
      .filter(Boolean)
      .join(", ") || "Carrier portal";
  const activeMobileWorkspace: MobileDispatchWorkspaceKind | null =
    panel?.kind ?? mobileWorkspace;
  const stageDispatchTasks = (candidates: CarrierTask[]) => {
    const next = mergeStagedTasks(stagedTasks, candidates).tasks;
    setStagedTasks(next);
    if (next.length > 0 && !compactViewport) setPanel({ kind: "assign" });
  };
  const removeDispatchTasks = (taskIds: string[]) => {
    const next = taskIds.reduce(
      (tasks, taskId) => removeStagedTask(tasks, taskId).tasks,
      stagedTasks
    );
    setStagedTasks(next);
    if (next.length === 0) setPanel(null);
  };
  const clearStagedTasks = () => {
    setStagedTasks([]);
    setPanel(null);
  };
  const openAssignPanel = (task: CarrierTask) => {
    stageDispatchTasks([task]);
  };
  const refetchDispatchData = () =>
    Promise.all([
      tasksQuery.refetch(),
      returningQuery.refetch(),
      tripsQuery.refetch(),
      operatorsQuery.refetch(),
      awaitingReceiptQuery.refetch(),
      timeWindowsQuery.refetch(),
      queryClient
        .invalidateQueries(
          { queryKey: ["carrier", "task-investigations"] },
          { throwOnError: true }
        )
        .then(
          () => ({ isError: false }),
          () => ({ isError: true })
        ),
    ]);
  const retryDispatchData = () => {
    void refetchDispatchData();
  };

  const openTrip = (trip: CarrierTrip) => {
    setPanel(null);
    setMobileWorkspace(null);
    setModalTrip(trip);
  };
  const openDriverAssignment = (trip: CarrierTrip) => {
    setPanel(null);
    setMobileWorkspace(null);
    setAssignDriverTrip(trip);
  };
  const openTaskFailure = (task: TaskFailureTarget) => {
    if (isTaskOnCancellationHold(task)) return;
    setPanel(null);
    setMobileWorkspace(null);
    setModalTrip(null);
    setFailureTask(task);
  };
  const changeMobileWorkspace = (next: MobileDispatchWorkspaceKind | null) => {
    if (next === "assign") {
      if (stagedTasks.length > 0) setPanel({ kind: "assign" });
      setMobileWorkspace(null);
      return;
    }
    if (next === "lookup") {
      setPanel({ kind: "lookup" });
      setMobileWorkspace(null);
      return;
    }
    setPanel(null);
    setMobileWorkspace(next);
  };
  const toggleTaskStaging = (task: CarrierTask) =>
    stagedTasks.some(({ id }) => id === task.id)
      ? removeDispatchTasks([task.id])
      : openAssignPanel(task);
  const operatorRailProps = {
    operators,
    returningSummary,
    onOpenReturning: setReturning,
    trips: activeTrips,
    unassignedTasks,
    awaitingReceiptTasks: visibleAwaitingReceiptTasks,
    isLoading: dataLoading,
    onOpenTrip: openTrip,
    onAssignDriver: openDriverAssignment,
    onAssignTask: toggleTaskStaging,
    onFailTask: openTaskFailure,
    stagingCenterId: stagedDispatchCenterId(stagedTasks),
    stagedTaskIds: new Set(stagedTasks.map((task) => task.id)),
  };
  const refreshDispatchData = async () => {
    if (refreshPending) return;
    setRefreshPending(true);
    setRefreshFeedback(null);
    try {
      const results = await refetchDispatchData();
      if (results.some((result) => result.isError)) {
        throw new Error("A Dispatch projection failed to refresh");
      }
      setRefreshFeedback("success");
      setFreshnessNow(Date.now());
    } catch {
      setRefreshFeedback("error");
    } finally {
      setRefreshPending(false);
    }
  };

  useEffect(() => {
    const timer = window.setInterval(() => setFreshnessNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const latestTasks = new Map(tasks.map((task) => [task.id, task]));
    setStagedTasks((current) => {
      const next = current.flatMap((task) => {
        const latest = latestTasks.get(task.id);
        return latest && isTaskSelectableForStaging(latest) ? [latest] : [];
      });
      if (
        next.length === current.length &&
        next.every((task, index) => task === current[index])
      ) {
        return current;
      }
      return next;
    });
  }, [tasks]);

  useEffect(() => {
    if (!compactViewport) return;
    setSelectionMode(false);
  }, [compactViewport]);

  useEffect(() => {
    if (stagedTasks.length === 0 && panel?.kind === "assign") {
      setPanel(null);
    }
  }, [panel?.kind, stagedTasks.length]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.altKey || returning !== null) return;
      const key = event.key.toLowerCase();
      if ((event.metaKey || event.ctrlKey) && key === "k") {
        event.preventDefault();
        setPanel({ kind: "lookup" });
        return;
      }

      if (event.key === "Escape") {
        setSelectionMode(false);
        if (compactViewport) {
          setPanel(null);
          setMobileWorkspace(null);
          return;
        }
        if (stagedTasks.length > 0 || panel?.kind === "assign") {
          setStagedTasks([]);
        }
        setPanel(null);
        return;
      }

      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.tagName === "SELECT" ||
          target.getAttribute("role") === "textbox" ||
          target.getAttribute("role") === "combobox" ||
          target.isContentEditable)
      ) {
        return;
      }
      const inShortcutWorkspace =
        target instanceof HTMLElement &&
        Boolean(target.closest("[data-dispatch-shortcuts]"));
      if (!inShortcutWorkspace) return;
      if (key === "f") {
        event.preventDefault();
        setPanel({ kind: "lookup" });
      } else if (key === "s") {
        event.preventDefault();
        setSelectionMode((current) => !current);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [compactViewport, panel?.kind, stagedTasks.length, returning]);

  if (me.isLoading && !me.data) {
    return (
      <PortalState
        title="Loading dispatch access"
        description="Checking your Carrier Dispatch permissions."
        loading
      >
        <DispatchExitButton />
      </PortalState>
    );
  }

  if (me.error && !me.data) {
    const permissionError = hasHttpStatus(me.error, 403);
    return (
      <PortalState
        title={
          permissionError
            ? "Dispatch access required"
            : "Unable to load dispatch access"
        }
        description={
          permissionError
            ? "This account does not have permission to view Carrier Dispatch for the selected scope."
            : "Carrier Dispatch permissions could not be checked. Try again after the connection recovers."
        }
      >
        <DispatchExitButton />
      </PortalState>
    );
  }

  if (!me.data) {
    return (
      <PortalState
        title="Unable to load dispatch access"
        description="Carrier Dispatch permissions are unavailable. Try again after your session reconnects."
      >
        <DispatchExitButton />
      </PortalState>
    );
  }

  if (forbidden) {
    return (
      <PortalState
        title="Dispatch access required"
        description="This account does not have permission to view Carrier Dispatch for the selected scope."
      >
        <DispatchExitButton />
      </PortalState>
    );
  }

  return (
    <div className="fixed inset-0 flex flex-col bg-[#eceaf3] text-zinc-900">
      <CommandBar
        lookupActive={panel?.kind === "lookup"}
        carrierLabel={carrierLabel}
        selectedDate={selectedDate}
        operationalTimeZone={operationalTimeZone}
        selectedWindow={selectedWindow}
        windows={windows}
        onDateChange={changeSelectedDate}
        onWindowChange={setSelectedWindow}
        onToggleLookup={() =>
          setPanel((current) =>
            current?.kind === "lookup" ? null : { kind: "lookup" }
          )
        }
        liveState={liveState}
        refreshPending={refreshPending}
        refreshFeedback={refreshFeedback}
        onRefresh={() => void refreshDispatchData()}
      />
      <TaskInvestigations />
      <CollectionExceptions tasks={[...tasks, ...awaitingReceiptTasks]} />
      <KpiStrip tasks={visibleTasks} trips={trips} operators={operators} />
      {dataError && !forbidden && (
        <DispatchInlineState
          title="Some dispatch data failed to load."
          description="Cached data remains visible where available."
          tone="error"
          actionLabel="Retry"
          onAction={retryDispatchData}
          className="mx-3 my-2 shrink-0"
        />
      )}
      <div className="flex min-h-0 flex-1">
        {!compactViewport && (
          <aside
            className="shrink-0 border-r border-zinc-200 bg-zinc-50 transition-[width]"
            style={{ width: railCollapsed ? 48 : 364 }}
          >
            <OperatorRail
              {...operatorRailProps}
              collapsed={railCollapsed}
              onToggleCollapsed={() => setRailCollapsed((value) => !value)}
            />
          </aside>
        )}

        <MapShell
          returningSummary={returningSummary}
          onOpenReturning={(centerId) => setReturning({ centerId })}
          tasks={visibleTasks}
          trips={activeTrips}
          operators={operators}
          onAssignTasks={stageDispatchTasks}
          onRemoveTasks={removeDispatchTasks}
          onOpenTrip={openTrip}
          stagedTasks={stagedTasks}
          stagingCenterId={stagedDispatchCenterId(stagedTasks)}
          selectionMode={selectionMode}
          onToggleSelectionMode={() => setSelectionMode((current) => !current)}
          onBoxSelection={(selectedTasks) => {
            setSelectionMode(false);
            stageDispatchTasks(selectedTasks);
          }}
          allowBoxSelection={!compactViewport}
          compact={compactViewport}
        />

        {!compactViewport && panel && (
          <aside
            className="shrink-0 border-l border-zinc-200 bg-white"
            style={{ width: 372 }}
          >
            {panel.kind === "assign" && (
              <AssignPanel
                tasks={stagedTasks}
                trips={activeTrips}
                onRemoveTask={(taskId) => removeDispatchTasks([taskId])}
                onClear={clearStagedTasks}
                onClose={clearStagedTasks}
                onAssigned={clearStagedTasks}
              />
            )}
            {panel.kind === "lookup" && (
              <LookupPanel
                trips={trips}
                onClose={() => setPanel(null)}
                onOpenTrip={(trip) => {
                  openTrip(trip);
                  setPanel(null);
                }}
              />
            )}
          </aside>
        )}
      </div>

      <Sheet
        open={returning !== null}
        onOpenChange={(open) => {
          if (!open) setReturning(null);
        }}
      >
        <SheetContent className="flex w-full flex-col sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>Returning to center</SheetTitle>
            <SheetDescription>
              Expected arrivals across all route dates. Last confirmed custody
              is not live location.
            </SheetDescription>
          </SheetHeader>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {returning !== null ? (
              <CenterIntakeObligationsSection
                key={dispatchCenterId ?? "all"}
                filter={returning}
                onRefresh={returningQuery.refetch}
              />
            ) : null}
          </div>
        </SheetContent>
      </Sheet>
      {compactViewport && (
        <MobileDispatchWorkspace
          active={activeMobileWorkspace}
          stagedCount={stagedTasks.length}
          fleetCount={operators.length}
          taskCount={
            unassignedTasks.length + visibleAwaitingReceiptTasks.length
          }
          onActiveChange={changeMobileWorkspace}
        >
          {(activeMobileWorkspace === "fleet" ||
            activeMobileWorkspace === "tasks") && (
            <OperatorRail
              {...operatorRailProps}
              initialTab={activeMobileWorkspace}
              activeTab={activeMobileWorkspace}
              onTabChange={changeMobileWorkspace}
              showHeader={false}
              collapsible={false}
            />
          )}
          {activeMobileWorkspace === "assign" && (
            <AssignPanel
              tasks={stagedTasks}
              trips={activeTrips}
              onRemoveTask={(taskId) => removeDispatchTasks([taskId])}
              onClear={clearStagedTasks}
              onClose={() => setPanel(null)}
              onAssigned={clearStagedTasks}
              showHeader={false}
            />
          )}
          {activeMobileWorkspace === "lookup" && (
            <LookupPanel
              trips={trips}
              onClose={() => setPanel(null)}
              onOpenTrip={openTrip}
              showHeader={false}
              replaceNavigation
            />
          )}
          {activeMobileWorkspace === "filters" && (
            <MobileDispatchFilters
              selectedDate={selectedDate}
              selectedWindow={selectedWindow}
              windows={windows}
              onDateChange={changeSelectedDate}
              onWindowChange={setSelectedWindow}
            />
          )}
        </MobileDispatchWorkspace>
      )}

      <TripFocusModal
        trip={modalTrip}
        open={Boolean(modalTrip)}
        onOpenChange={(open) => !open && setModalTrip(null)}
        onFailTask={openTaskFailure}
      />
      <TaskFailureDialog
        task={failureTask}
        open={Boolean(failureTask)}
        onOpenChange={(open) => !open && setFailureTask(null)}
      />
      {assignDriverTrip && (
        <AssignOperatorDialog
          trip={assignDriverTrip}
          open={Boolean(assignDriverTrip)}
          onOpenChange={(open) => !open && setAssignDriverTrip(null)}
          dialogPresentation="responsive-sheet"
          responsiveSheetAt="tablet"
        />
      )}
    </div>
  );
}

function MobileDispatchFilters({
  selectedDate,
  selectedWindow,
  windows,
  onDateChange,
  onWindowChange,
}: {
  selectedDate: string;
  selectedWindow: DispatchWindowFilter;
  windows: DispatchWindowOption[];
  onDateChange: (date: string) => void;
  onWindowChange: (window: DispatchWindowFilter) => void;
}) {
  return (
    <div className="h-full overflow-y-auto px-4 pb-8 pt-4">
      <div className="mx-auto max-w-lg space-y-5">
        <p className="text-sm text-muted-foreground">
          Filters apply to the map, Fleet, Tasks, and assignment workspace.
        </p>
        <div className="space-y-2">
          <span className="text-sm font-medium">Dispatch center</span>
          <DispatchCenterFilter
            className="w-full"
            triggerClassName="h-11 w-full min-w-0"
          />
        </div>
        <label htmlFor="mobile-dispatch-date" className="block space-y-2">
          <span className="text-sm font-medium">Dispatch date</span>
          <DispatchDateInput
            id="mobile-dispatch-date"
            selectedDate={selectedDate}
            onDateChange={onDateChange}
            className="h-11"
          />
        </label>
        <label htmlFor="mobile-dispatch-window" className="block space-y-2">
          <span className="text-sm font-medium">Time window</span>
          <NativeSelect
            aria-label="Time window filter"
            id="mobile-dispatch-window"
            value={dispatchWindowFilterValue(selectedWindow)}
            onChange={(event) =>
              onWindowChange(parseDispatchWindowFilter(event.target.value))
            }
            className="h-11 w-full"
          >
            <option value="all">All windows</option>
            {windows.map((window) => (
              <option key={window.value} value={window.value}>
                {window.label}
              </option>
            ))}
            <option value="overdue">Overdue</option>
          </NativeSelect>
        </label>
      </div>
    </div>
  );
}

export default function DispatchV2Screen() {
  return (
    <ProtectedRoute>
      <DispatchV2Content />
    </ProtectedRoute>
  );
}
